import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  CircularProgress,
} from '@mui/material';
import NewModal from '../../../Shared/NewModal';
import { ReactComponent as DeleteIcon } from '../../../../images/icons/delete_icon.svg';
import { ReactComponent as PreDeleteIcon } from '../../../../images/icons/delete_pre_icon.svg';
import { ReactComponent as DeleteSuccessIcon } from '../../../../images/icons/delete_success_icon.svg';
import { ReactComponent as DeleteFailedIcon } from '../../../../images/icons/delete_failed_icon.svg';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import {
  clearCustomerActionStatus,
  deleteUser,
} from '../../../../store/customers/actionCreators';
import ProgressBar from '../../../Shared/ProgressBar';
import { dimGrey, lightBlack, lightTeal } from '../../../../mainTheme';

const typography = {
  heading: {
    fontWeight: 500,
    fontSize: '22px',
    lineHeight: '30px',
    letterSpacing: '0.36px',
  },
  subText: {
    fontWeight: 500,
    fontSize: '16px',
    lineHeight: '22px',
    letterSpacing: '0.2px',
    color: dimGrey,
  },
} as const;

const REASONS = [
  'No longer need medication assistance',
  'Found a better way to track medication adherence',
  'Received too many notifications',
  'Taking a break',
  'Other',
];

interface PatientDeleteDialogProps {
  onClose: () => void;
  patientId: number;
  onSuccess: () => void;
}

const StatusView: React.FC<{
  icon: React.ReactElement;
  title: string;
  message: string;
}> = ({ icon, title, message }) => (
  <Box display="flex" flexDirection="column" alignItems="center">
    {icon}
    <Typography sx={{ ...typography.heading, mt: 2 }}>{title}</Typography>
    <Typography sx={{ ...typography.subText, textAlign: 'center', mt: 1 }}>
      {message}
    </Typography>
  </Box>
);

const ReasonStep: React.FC<{
  reason: string;
  setReason: (r: string) => void;
  details: string;
  setDetails: (d: string) => void;
}> = ({ reason, setReason, details, setDetails }) => (
  <Box>
    <Box display="flex" flexDirection="column" mb="24px">
      <Box mb="16px">
        <ProgressBar currentStep={1} totalSteps={2} />
      </Box>
      <PreDeleteIcon />
      <Typography sx={typography.heading} mt={2}>
        Delete patient
      </Typography>
      <Typography sx={typography.subText} mt={1}>
        Tell us why you're deleting the account.
      </Typography>
    </Box>
    <Typography
      sx={{
        ...typography.subText,
        fontWeight: 700,
        color: lightBlack,
        mb: '14px',
      }}
    >
      Select reason{' '}
      <span style={{ fontWeight: 400, color: typography.subText.color }}>
        (optional)
      </span>
    </Typography>
    <RadioGroup value={reason} onChange={(e) => setReason(e.target.value)}>
      {REASONS.map((r) => (
        <FormControlLabel
          key={r}
          value={r}
          control={
            <Radio
              sx={{
                color: dimGrey,
                '&.Mui-checked': {
                  color: lightTeal,
                },
              }}
            />
          }
          label={r}
          sx={{
            alignItems: 'center',
            gap: '8px',
            '.MuiTypography-root': {
              color: lightBlack,
              fontSize: '14px',
              fontWeight: 500,
              lineHeight: '20px',
              letterSpacing: '0.2px',
            },
          }}
        />
      ))}
    </RadioGroup>
    <Typography
      sx={{
        ...typography.subText,
        fontWeight: 700,
        color: lightBlack,
        mb: '8px',
        mt: '14px',
      }}
    >
      Add details{' '}
      <span style={{ fontWeight: 400, color: typography.subText.color }}>
        (Optional)
      </span>
    </Typography>
    <TextField
      fullWidth
      multiline
      rows={3}
      value={details}
      onChange={(e) => setDetails(e.target.value)}
      placeholder="Enter more details here"
      sx={{
        '& .MuiInputBase-root': {
          borderRadius: '24px',
          border: '1px solid #D4D4D9',
          padding: 0,
          overflow: 'auto',
          '& textarea': {
            padding: '12px',
            fontSize: '16px',
            fontWeight: 500,
            lineHeight: '22px',
            letterSpacing: '0.2px',
            color: lightBlack,
            resize: 'none',
            overflow: 'auto',
            '&::placeholder': {
              color: '#9999A3',
              fontSize: '16px',
              fontWeight: 500,
              lineHeight: '22px',
              letterSpacing: '0.2px',
            },
          },
        },
        '& .MuiOutlinedInput-notchedOutline': {
          border: 'none',
        },
      }}
      InputProps={{
        sx: {
          height: '98px',
          minHeight: '98px',
          maxHeight: '160px',
          overflow: 'auto',
        },
      }}
    />
  </Box>
);

const ConfirmStep: React.FC<{
  confirmationText: string;
  setConfirmationText: (t: string) => void;
}> = ({ confirmationText, setConfirmationText }) => (
  <Box>
    <Box display="flex" flexDirection="column" mb="24px">
      <Box mb="16px">
        <ProgressBar currentStep={2} totalSteps={2} />
      </Box>
      <DeleteIcon />
      <Typography sx={typography.heading} mt={2}>
        Confirm deletion
      </Typography>
      <Typography sx={typography.subText} mt={1}>
        The patient will be deleted permanently.
      </Typography>
    </Box>
    <Typography sx={{ ...typography.subText, fontWeight: 700, ml: 2 }}>
      Type the word "delete" to confirm.
    </Typography>
    <TextField
      fullWidth
      variant="outlined"
      value={confirmationText}
      placeholder='Type "delete"'
      onChange={(e) => setConfirmationText(e.target.value)}
      sx={{
        mt: '8px',
        '& .MuiOutlinedInput-root': { borderRadius: '8px' },
        '& .MuiInputBase-input': {
          color: dimGrey,
          fontWeight: 500,
          fontSize: '16px',
          lineHeight: '20px',
          letterSpacing: '0.2px',
          '&::placeholder': {
            color: '#9999A3',
            opacity: 1,
          },
        },
      }}
    />
  </Box>
);

const PatientDeleteDialog: React.FC<PatientDeleteDialogProps> = ({
  onClose,
  patientId,
  onSuccess,
}) => {
  const dispatch = useDispatch();

  const { actionStatus, isLoading: isDeletingPatient } = useSelector(
    (state: RootState) => ({
      actionStatus: state.customers.actionStatus,
      isLoading: state.customers.isLoading,
    }),
  );

  const [step, setStep] = useState<'reason' | 'confirm' | 'success' | 'error'>(
    'reason',
  );
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [confirmationText, setConfirmationText] = useState('');

  const handleDelete = () => {
    dispatch(
      deleteUser(
        patientId,
        {
          deletion_reason: reason,
          additional_details: details,
        },
        false, // showToast: false, UI handles feedback
      ),
    );
  };

  // Handles API result
  useEffect(() => {
    if (step === 'confirm') {
      if (actionStatus.success) {
        setStep('success');
      } else if (actionStatus.success === false && actionStatus.error) {
        setStep('error');
      }
    }
  }, [actionStatus, step]);

  const handleReset = () => {
    setStep('reason');
    setReason('');
    setDetails('');
    setConfirmationText('');
    dispatch(clearCustomerActionStatus());
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  let content;
  let actions;

  switch (step) {
    case 'reason':
      content = (
        <ReasonStep
          reason={reason}
          setReason={setReason}
          details={details}
          setDetails={setDetails}
        />
      );
      actions = {
        primaryAction: {
          text: 'Next',
          onClick: () => setStep('confirm'),
        },
        secondaryAction: {
          text: 'Cancel',
          onClick: handleClose,
        },
      };
      break;
    case 'confirm':
      content = (
        <ConfirmStep
          confirmationText={confirmationText}
          setConfirmationText={setConfirmationText}
        />
      );
      actions = {
        primaryAction: {
          text: 'Delete',
          onClick: handleDelete,
          disabled: confirmationText !== 'delete' || isDeletingPatient,
        },
        secondaryAction: {
          text: 'Cancel',
          onClick: handleClose,
        },
      };
      break;
    case 'success':
      content = (
        <StatusView
          icon={<DeleteSuccessIcon />}
          title="Patient deleted successfully"
          message="You'll no longer see this patient."
        />
      );
      actions = {
        primaryAction: {
          text: 'OK',
          onClick: () => {
            handleReset();
            if (onSuccess) onSuccess();
          },
        },
      };
      break;
    case 'error':
      content = (
        <StatusView
          icon={<DeleteFailedIcon />}
          title="Deletion unsuccessful"
          message="The patient didn't delete. Please try again."
        />
      );
      actions = {
        primaryAction: {
          text: 'Try again',
          onClick: handleReset,
        },
      };
      break;
    default:
      content = null;
      actions = {};
  }

  return (
    <NewModal
      showCloseX
      title="Delete patient"
      onClose={handleClose}
      maxWidth="xs"
      actions={actions}
    >
      {isDeletingPatient ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={200}
        >
          <CircularProgress />
        </Box>
      ) : (
        content
      )}
    </NewModal>
  );
};

export default PatientDeleteDialog;
